import React, { useEffect, useState, useCallback } from 'react'
import { connect, useDispatch } from 'react-redux'

import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state/types'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'

import { NEW_CITATION_LIST_MODAL } from '@chegg-wt/core/state/modals/constants'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  setGenerateNewCitationList,
  createProject,
} from '@chegg-wt/core/state/currentProject/actions'
import { Config } from '@chegg-wt/core/config'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import {
  currentUrlStyleNameSelector,
  currentStyleNameSelector,
  generateNewCitationListSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { removeUpgradeCitationFromSessionStorage } from '@chegg-wt/core/utils/upgrade'
import AppLink from '@chegg-wt/core/components/AppLink'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  ComponentPayload,
  componentOrigins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import {
  UserClickPayload,
  origins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { createCitationSourceTypeSelector } from '@chegg-wt/core/state/createCitation/selectors'

import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { AuthPayload } from '@chegg-wt/core/types/auth'
import { AuthModalOrigin } from '../AuthModal/types'
import {
  CONTINUE_TEXT,
  FOOTER_BANNER_CTA_TEXT,
  FOOTER_BANNER_TEXT,
  START_TRIAL_TEXT,
  NEW_CITATION_LIST_MODAL_ID,
  TITLE,
  INFO_TEXT,
} from './constants'
import {
  HackyModal,
  ModalContents,
  FreeCTA,
  HackyFooter,
  ContinueCTA,
  InfoText,
  CTAContainer,
} from './styled'
import { SmallNotificationBanner } from './SmallNotificationBanner'

import { CONTINUE_CTA, FREE_CTA, SIGN_IN, SIGN_IN_BUTTON } from './testIds'

interface NewCitationListModalProps {
  isLoggedIn: boolean
  modalData: {
    withAuthBanner: boolean
    alwaysParticipate?: boolean
  }
  startAuthFlow: (payload: AuthPayload) => void
  closeModal: (name: string, origin?: string) => void
  generateNewCitationList: boolean
  newStyle: string
  currentStyleName: string
  emitComponentViewEvent: (payload: ComponentPayload) => void
  currentSourceType: string
  userClick: (payload: UserClickPayload) => void
}

const MODAL_ANIMATION_DURATION_MS = 300

export const NewCitationListModal: React.FC<NewCitationListModalProps> = ({
  isLoggedIn,
  startAuthFlow,
  closeModal,
  modalData,
  newStyle,
  currentStyleName,
  generateNewCitationList,
  currentSourceType,
  emitComponentViewEvent,
  userClick,
}) => {
  const dispatch = useDispatch()
  const { toUpgrade } = useRoutesContext()

  const ctaText = Config.ctfmUpgradeCTAText ?? START_TRIAL_TEXT

  const [isOpen, setIsOpen] = useState(true)
  const [isSAModalOpen, setIsSAModalOpen] = useState(false)

  const onSignInClick = () => {
    setIsOpen(false)
    setIsSAModalOpen(true)
  }

  useEffect(() => {
    emitComponentViewEvent({
      origin: componentOrigins.newCitationListModal,
      metadata: {
        source: currentSourceType,
        style: currentStyleName,
      },
    })
  }, [])

  useEffect(() => {
    if (!isSAModalOpen) {
      return
    }

    const asyncOpenId = setTimeout(() => {
      startAuthFlow({
        defaultFlow: 'signin',
        origin: AuthModalOrigin.NEW_CITATION_LIST_MODAL,
        alwaysParticipate: modalData.alwaysParticipate,
      })
    }, MODAL_ANIMATION_DURATION_MS)

    return () => {
      clearTimeout(asyncOpenId)
    }
  }, [isSAModalOpen])

  useEffect(() => {
    if (isOpen) {
      return
    }

    const asyncCloseId = setTimeout(
      () => !isSAModalOpen && closeModal(NEW_CITATION_LIST_MODAL),
      MODAL_ANIMATION_DURATION_MS
    )

    return () => {
      clearTimeout(asyncCloseId)
    }
  }, [isOpen])

  const handleContinueClick = () => {
    userClick({ origin: origins.newCitationListModalCreateClick })
    if (!generateNewCitationList) dispatch(setGenerateNewCitationList(true))
    dispatch(createProject())
    closeModal(NEW_CITATION_LIST_MODAL)
  }

  const closeHackyModal = useCallback(() => {
    setIsOpen(false)
    removeUpgradeCitationFromSessionStorage()
  }, [])

  return (
    <ClientOnlyPortal>
      <HackyModal
        id={NEW_CITATION_LIST_MODAL_ID}
        isOpen={isOpen}
        onClose={closeHackyModal}
        shouldCloseOnClickOutside
        title={TITLE}
      >
        <>
          <ModalContents>
            <InfoText>{INFO_TEXT(newStyle, currentStyleName)}</InfoText>
            <CTAContainer>
              <ContinueCTA data-test={CONTINUE_CTA} onClick={handleContinueClick}>
                {CONTINUE_TEXT}
              </ContinueCTA>
              <AppLink route={toUpgrade()}>
                <FreeCTA
                  href="I'm needed because TS complains otherwise, AppLink replaces me when rendering"
                  data-test={FREE_CTA}
                  onClick={() =>
                    userClick({ origin: origins.newCitationListModalStartClick })
                  }
                >
                  {ctaText}
                </FreeCTA>
              </AppLink>
            </CTAContainer>
          </ModalContents>
          {modalData.withAuthBanner && !isLoggedIn ? (
            <HackyFooter>
              <SmallNotificationBanner
                message={FOOTER_BANNER_TEXT}
                ctaText={FOOTER_BANNER_CTA_TEXT}
                onSignInClick={onSignInClick}
                data-test={SIGN_IN}
                SIGN_IN_BUTTON={SIGN_IN_BUTTON}
              />
            </HackyFooter>
          ) : null}
        </>
      </HackyModal>
    </ClientOnlyPortal>
  )
}

const mapStateToProps = (state: AppState) => ({
  isLoggedIn: loggedInSelector(state),
  modalData: modalDataSelector(state),
  newStyle: currentUrlStyleNameSelector(state),
  currentStyleName: currentStyleNameSelector(state),
  generateNewCitationList: generateNewCitationListSelector(state),
  currentSourceType: createCitationSourceTypeSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  emitComponentViewEvent,
  userClick,
  startAuthFlow,
}

export default connect(mapStateToProps, mapDispatchToProps)(NewCitationListModal)
